// eslint-disable-next-line no-use-before-define
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useFormikContext, FormikProps } from 'formik';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { styled } from '@mui/system';
import dayjs from 'op-utils/dayjs';
import { DateValidationError } from '@mui/x-date-pickers';
import { LoadingSpinner } from 'shared-components/components';
import { Gender, Region } from 'shared-components/enums';
import {
  ROTextField,
  RODatePicker,
  ROAutocomplete,
  ROToggleButtons,
  SelectOptionType,
  CitationButton,
} from 'shared-components/components/FormFields';
import { Logger } from 'shared-components/utils';
import { codeToIntakePageMapping, getOptionByValue } from '../DiagnosisPages/Utils';
import ROPatientCarePathSidePanel from '../SidePanel/SidePanel';
import {
  BOOLEAN_OPTIONS,
  BOOLEAN_UNKNOWN_OPTIONS,
  PATIENT_STATUS_OPTIONS,
  ListOptionType,
  DELAY_CATEGORY_OPTIONS,
  SCALE_OPTIONS,
  ECOG_PERFORMANCE_STATUS_OPTIONS,
  KPS_PERFORMANCE_STATUS_OPTIONS,
  KARNOFSKY_ABILITY_LEVELS,
  PROXIMITY_OPTIONS,
} from './Interface';
import ROPatientCarePlanPageFooter from '../Footer';
import { LOAD_TREATMENTINFO, UPDATE_TREATMENTINFO, LOAD_TREATMENT_LIST_DATA, TENANT_CONFIG_QUERY } from './Queries';
import { GET_DIAGNOSIS_FROM_CAREPLAN } from '../SidePanel/SidePanelQueries';
import ROPatientCarePlanContext from '../ROPatientCarePlanContext';
import { CAREPLAN_PAGES } from '../Constants';
import { validateField, isPageValid } from '../ValidationEngine';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import { CareplanBanners } from '../Banner/Banner';
import { GET_CAREPLAN } from '../Queries';
import CareplanContainer from '../Container';
import { ROPatientContext } from '../../PatientSummary/context';
import { priorityTooltip, isRcrCodeInRange, tooltipIconWrapper } from '../helpers';
import { logMissingDiagnosisCode, ScrollToError } from '../Common';
import './TreatmentInfoPage.scss';
import { Typography, Box, TableContainer, TableHead, Table, TableRow, TableCell, TableBody } from '@mui/material';
import { Dayjs } from 'op-utils/dayjs';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { StandardDialog } from 'shared-components/components';
import ModalBody from './RCRModal';
import CitationOverlay from '../../AiSummary/components/CitationOverlay';
import type { CitationProps } from '../../AiSummary/components/CitationPanel';
import { UserContext } from 'op-contexts/index';
import { FindInPageOutlined as FindInPageOutlinedIcon } from '@mui/icons-material';
interface TreatmentInfoCitationItem {
  url?: string | null;
  displayName?: string | null;
  filepath?: string | null;
  filename?: string | null;
  extension?: string | null;
}

const REACT_APP_REGION = import.meta.env.REACT_APP_REGION;

const logger = new Logger('ROPatientTreatmentInfoPage');
interface State {
  chemoWhereOptions: ListOptionType[];
  intentOptions: ListOptionType[];
  chemoSequencingOptions: ListOptionType[];
  treatingDepartmentOptions: ListOptionType[];
  emdtOptions: ListOptionType[];
  brachyWhereOptions: ListOptionType[];
  brachySequencingOptions: ListOptionType[];
  priorityOptions: ListOptionType[];
  consentOptions: ListOptionType[];
  pharmacyPrescriptionOptions: ListOptionType[];
}

export const LIST_OPTIONS = {
  INTENT: 'intent',
  TREATING_DEPARTMENT: 'treatingDepartment',
  CHEMO_WHERE: 'chemoWhere',
  CHEMO_SEQUENCING: 'chemoSequencing',
  PREVIOUS_MDT: 'previousMdt',
  BRACHY_WHERE: 'brachyWhere',
  BRACHY_SEQUENCING: 'brachySequencing',
  PRIORITY: 'priority',
  CONSENT_STATUS: 'consentStatus',
  PHARMACY_PRESCRIPTION: 'pharmacyPrescription',
};

const StyledQuestionCircleIcon = styled(HelpOutlineIcon)`
  cursor: pointer;
`;

const region = REACT_APP_REGION;

const filterListdata = (data: any, listCategory: string, sortOnWeight: boolean = false): any => {
  const listData = [
    ...data.carepathDataReferenceList.filter((options: any): boolean => options.category.category === listCategory),
  ];
  const sortedListData = sortOnWeight
    ? listData.sort((a: any, b: any) => a.listWeight - b.listWeight)
    : listData.sort((a: any, b: any) => a.data.option.localeCompare(b.data.option));
  return sortedListData.map((option: any) => ({
    label: option.data.option === 'MRDRO' ? 'Murdoch' : option.data.option,
    value: option.data.option,
  }));
};

export const previousURL = (
  diagnosis: any,
  careplan: any,
  patientId: string,
  careplanId: string,
  careplanState?: any,
): string => {
  /*
    1. Primary diagnosis page
    2. Secondary diagnosis page
    3. Staging page
    4. intake page
  */
  const primaryDiagnosis = diagnosis.isPrimaryDiagnosis === true ? diagnosis : diagnosis.relatedPrimaryDiagnosis;
  const metastasisDiagnosis = diagnosis.isPrimaryDiagnosis === false ? diagnosis : null;
  if (!primaryDiagnosis && !metastasisDiagnosis) {
    return '';
  }
  const hasIntakePage =
    primaryDiagnosis &&
    primaryDiagnosis.diagnosisCode &&
    primaryDiagnosis.diagnosisCode.diagnosisCode &&
    codeToIntakePageMapping(primaryDiagnosis.diagnosisCode.diagnosisCode, careplanState.intakePageMapping) !== '';

  if (careplan.isMetsDiagnosisOwner && !careplan.isPrimDiagnosisOwner) {
    return `/radiation/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosis.id}/${CAREPLAN_PAGES.STAGING}`;
  }

  if (careplan.isPrimDiagnosisOwner || careplan.isMetsDiagnosisOwner) {
    // Intake is part of primary diagnosis, that diagnosis must be a draft
    if (hasIntakePage) {
      return `/radiation/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosis.id}/${CAREPLAN_PAGES.INTAKE}`;
    }
    return `/radiation/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosis.id}/${CAREPLAN_PAGES.STAGING}`;
  }
  if (!careplan.isPrimDiagnosisOwner && !careplan.isMetsDiagnosisOwner) {
    if (metastasisDiagnosis) {
      return `/radiation/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosis.id}/metastasis`;
    }
    if (primaryDiagnosis) {
      return `/radiation/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosis.id}/primary`;
    }
  }

  logger.error('previousURL', [
    'Treatment Info Page: Unable to determine the previous URL this user should be directed to.',
    diagnosis,
  ]);
  return '';
};

const ROPatientTreatmentInfoPage = (): JSX.Element => {
  const { patientDetails } = useContext(ROPatientContext);
  const userContext = useContext(UserContext);
  const { hasFeature } = userContext;
  const isSimpleDiagnosisSelectionEnabled = hasFeature('SimpleDiagnosisSelection');
  const isAIPrepopulateEnabled = hasFeature('AI Prepopulate');
  const tenantAppConfig = useTenantAppConfig();
  const practitionerTenant = userContext.state.userState;
  const history = useHistory();
  const match: { params: { id: string; careplanId: string } } = useRouteMatch();
  const [state, setState] = useState<State>({
    chemoWhereOptions: [],
    intentOptions: [],
    chemoSequencingOptions: [],
    treatingDepartmentOptions: [],
    emdtOptions: [],
    brachyWhereOptions: [],
    brachySequencingOptions: [],
    priorityOptions: [],
    consentOptions: [],
    pharmacyPrescriptionOptions: [],
  });
  const [rcrModalStatus, setRCRModalStatus] = useState<boolean>(false);
  const [showKarnofskyModal, setShowKarnofskyModal] = useState<boolean>(false);
  const { id: patientId, careplanId } = match.params;
  const [tumourStreamState, setTumourStreamState] = useState('');
  const {
    data: diagnosisData,
    error: diagLoadingError,
    loading: diagLoading,
  } = useQuery(GET_DIAGNOSIS_FROM_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { careplanId: careplanId },
    onCompleted: (data: any): void => {
      logMissingDiagnosisCode(logger, data.careplan, 'Treatment Info');
      setTumourStreamState(data.careplan.diagnosis?.diagnosisCode?.tumourStream?.name);
    },
  });
  const { values, setFieldValue }: FormikProps<any> = useFormikContext();

  const { state: careplanState } = useContext(ROPatientCarePlanContext);

  const treatmentInfo = values?.treatmentInfo || {};

  // Jank text fields, need to refactor this form at some point
  const [inPatientAt, setInPatientAt] = useState(treatmentInfo?.inPatient);
  const [otherCiedInstructions, setOtherCiedInstructions] = useState(treatmentInfo?.otherCiedInstructions);
  const [pharmacyPrescriptionOther, setPharmacyPrescriptionOther] = useState(treatmentInfo?.pharmacyPrescriptionOther);
  const [hormonesRegime, setHormonesRegime] = useState(treatmentInfo?.hormonesRegime);
  const [brachyDose, setBrachyDose] = useState(treatmentInfo?.brachyDose);
  const [startDateAdditionalInformation, setStartDateAdditionalInformation] = useState(
    treatmentInfo?.startDateAdditionalInformation,
  );
  const [previousTreatmentAdditionalInformation, setPreviousTreatmentAdditionalInformation] = useState(
    treatmentInfo?.previousTreatmentAdditionalInformation,
  );
  const [previousTreatmentAt, setPreviousTreatmentAt] = useState(treatmentInfo?.previousTreatmentAt);
  const [chemoRegime, setChemoRegime] = useState(treatmentInfo?.chemoRegime);
  const [customChemoWhere, setCustomChemoWhere] = useState(treatmentInfo?.customChemoWhere);
  const [chiefComplaint, setChiefComplaint] = useState(treatmentInfo?.chiefComplaint);
  const [impressionPlan, setImpressionPlan] = useState(treatmentInfo?.impressionPlan);
  const [proximityToPreviousTreatment, setProximityToPreviousTreatment] = useState(
    treatmentInfo?.proximityToPreviousTreatment,
  );

  const [pressedContinue, setPressedContinue] = useState(false);
  const isFieldValid = (field: any, fieldName?: any) => {
    if (!pressedContinue) {
      return true;
    }

    // Enforce an error when linac required is YES, because we don't support that
    if (fieldName === 'ciedLinacRequired' && field === 'yes') {
      return false;
    }
    return validateField(field);
  };

  const treatmentInfoQueryVariables = {
    id: careplanId,
    forTreatmentInfoPrepopulate: isAIPrepopulateEnabled,
  };

  const [updateTreatmentInfo] = useMutation(UPDATE_TREATMENTINFO, {
    refetchQueries: [
      {
        query: LOAD_TREATMENTINFO,
        variables: treatmentInfoQueryVariables,
      },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
  });

  const [selectedDateError, setSelectedDateError] = useState<DateValidationError | null>(null);

  const treatmentInfoPrepopulate = values?.treatmentInfoPrepopulate ?? null;

  const [citationOverlay, setCitationOverlay] = useState<{
    open: boolean;
    citations: CitationProps[];
    activeIndex: number;
  }>({ open: false, citations: [], activeIndex: 0 });

  const sourceDocumentsToCitations = (documents: TreatmentInfoCitationItem[]): CitationProps[] =>
    (documents ?? []).map((doc) => ({
      documentUrl: doc.url ?? undefined,
      title: doc.displayName ?? doc.filename ?? 'Document',
      documentType: (doc.extension === 'pdf' ? 'pdf' : doc.extension) ?? 'DocumentPart',
    }));

  const openCitation = (title: string, documents: TreatmentInfoCitationItem[]): void => {
    setCitationOverlay({
      open: true,
      citations: sourceDocumentsToCitations(documents),
      activeIndex: 0,
    });
  };

  const patientIsInSA = patientDetails?.patientState === 'SA';
  const tumourStreamHasDefault = ['Benign', 'Gynae', 'H&N', 'Brain', 'Urogenital', 'Metastasis'].includes(
    tumourStreamState,
  );
  const tumourStreamIsHN = tumourStreamState === 'H&N';
  const ICDTAuthorized = tenantAppConfig.TreatmentInfoPage.AuthorizedICDTreatingDepartments.includes(
    treatmentInfo.treatingDepartment,
  );

  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);

  const validationDependencyStructure = [
    {
      parent: null,
      expansionValue: null,
      children: tenantAppConfig.TreatmentInfoPage.mandatoryFields,
    },
    {
      parent: null,
      expansionValue: null,
      expansionCondition: () => tenantAppConfig.TreatmentInfoPage.MDT,
      children: ['previousMdt'],
    },
    {
      parent: null,
      expansionValue: null,
      expansionCondition: () => tenantConfigData?.tenantConfig?.treatmentInfoPageDelayCategory,
      children: ['delayCategory'],
    },
    {
      parent: null,
      expansionValue: null,
      children: ['priority'],
    },
    {
      parent: 'previousRadioTreatment',
      expansionValue: 'yes',
      expansionCondition: () => true,
      children: ['proximityToPreviousTreatment'],
    },
    {
      parent: 'cied',
      expansionValue: 'cied',
      expansionCondition: () => region === Region.UK,
      children: ['ciedPatientDependancy', 'ciedLinacRequired'],
    },
    {
      parent: 'cied',
      expansionValue: 'yes',
      expansionCondition: () => region === Region.AU,
      children: ['ciedProtocolRequired'],
    },
    // The child attribute of PREVENT_CONTINUE doesn't exist, therefore it will never meet validation requirements and always prevent the user from continuing
    {
      parent: 'cied',
      expansionValue: 'icd',
      expansionCondition: () => region === Region.UK && !ICDTAuthorized,
      children: ['PREVENT_CONTINUE'],
    },
    {
      parent: 'ciedPatientDependancy',
      expansionValue: null,
      expansionCondition: () => region === Region.UK,
      children: [],
    },
    {
      parent: 'ciedLinacRequired',
      expansionValue: 'yes',
      expansionCondition: () => region === Region.UK,
      children: ['PREVENT_CONTINUE'],
    },
    {
      parent: null,
      expansionValue: null,
      expansionCondition: () => patientIsInSA,
      children: ['cardioonc'],
    },
    {
      parent: null,
      expansionValue: null,
      expansionCondition: () => patientIsInSA && tumourStreamIsHN,
      children: ['endoscope'],
    },
    {
      parent: 'performanceScaleType',
      expansionValue: 'ecog',
      children: ['performanceScaleRating'],
    },
    {
      parent: 'performanceScaleType',
      expansionValue: 'kps',
      children: ['performanceScaleRating'],
    },
    {
      parent: 'pharmacyPrescription',
      expansionValue: 'Other',
      children: ['pharmacyPrescriptionOther'],
    },
  ];
  const { data: careplan } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });

  const { loading: listRefLoading, data: listRefData } = useQuery(LOAD_TREATMENT_LIST_DATA, {
    variables: {
      listCategory: [
        LIST_OPTIONS.CHEMO_WHERE,
        LIST_OPTIONS.INTENT,
        LIST_OPTIONS.TREATING_DEPARTMENT,
        LIST_OPTIONS.CHEMO_SEQUENCING,
        LIST_OPTIONS.PREVIOUS_MDT,
        LIST_OPTIONS.BRACHY_WHERE,
        LIST_OPTIONS.BRACHY_SEQUENCING,
        LIST_OPTIONS.PRIORITY,
        LIST_OPTIONS.CONSENT_STATUS,
        LIST_OPTIONS.PHARMACY_PRESCRIPTION,
      ],
      patientId,
    },
    // skip: !groupNumber && groupNumber === '',
    onCompleted: (data: any): void => {
      const chemoSequencing = filterListdata(data, LIST_OPTIONS.CHEMO_SEQUENCING);
      const chemoWhere = filterListdata(data, LIST_OPTIONS.CHEMO_WHERE);
      const intent = filterListdata(data, LIST_OPTIONS.INTENT);
      const treatingDepartment = filterListdata(data, LIST_OPTIONS.TREATING_DEPARTMENT);
      const mdtOptions = filterListdata(data, LIST_OPTIONS.PREVIOUS_MDT);
      const brachyWhereOptions = filterListdata(data, LIST_OPTIONS.BRACHY_WHERE);
      const brachySequencingOptions = filterListdata(data, LIST_OPTIONS.BRACHY_SEQUENCING);
      const priorityOptions = filterListdata(data, LIST_OPTIONS.PRIORITY, true);
      const consentOptions = filterListdata(data, LIST_OPTIONS.CONSENT_STATUS, true);
      const pharmacyPrescriptionOptions = filterListdata(data, LIST_OPTIONS.PHARMACY_PRESCRIPTION, true);
      const newState: State = { ...state };
      newState['chemoWhereOptions'] = chemoWhere;
      newState['chemoSequencingOptions'] = chemoSequencing;
      newState['intentOptions'] = intent;
      newState['treatingDepartmentOptions'] = treatingDepartment;
      newState['emdtOptions'] = mdtOptions;
      newState['brachyWhereOptions'] = brachyWhereOptions;
      newState['brachySequencingOptions'] = brachySequencingOptions;
      newState['priorityOptions'] = priorityOptions;
      newState['consentOptions'] = consentOptions;
      newState['pharmacyPrescriptionOptions'] = pharmacyPrescriptionOptions;
      setState(newState);
    },
    fetchPolicy: 'cache-and-network',
  });
  const [, updateState] = useState();
  // @ts-ignore allow empty object
  const forceUpdate = useCallback(() => updateState({}), []);

  useEffect(() => {
    const errorElems = document.getElementsByClassName('icon-form-error');
    if (errorElems && errorElems.length > 0) {
      errorElems[0].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      (errorElems[0] as HTMLElement).focus({ preventScroll: true });
    }
  }, [pressedContinue]);
  const [cardioOncState, setCardioOncState] = useState('');
  const [endoscopeState, setEndoscopeState] = useState('');

  const diagnosis = diagnosisData?.careplan?.diagnosis;
  const primaryDiagnosis = diagnosis?.isPrimaryDiagnosis === true ? diagnosis : diagnosis?.relatedPrimaryDiagnosis;
  const diagnosisCode = primaryDiagnosis?.diagnosisCode?.diagnosisCode;
  const formattedDxCode = Number(diagnosisCode?.substring(1));
  const formattedMorphologyCode = Number(primaryDiagnosis?.morphology?.code.substring(0, 4));
  const isValidCat1RCR = isRcrCodeInRange(formattedDxCode, formattedMorphologyCode);
  const getNewDelayCategory = () => {
    if (treatmentInfo.intent === 'Palliative' || !['C', 'D'].includes(diagnosisCode?.charAt(0))) {
      return 'Category 3';
    } else if (isValidCat1RCR) {
      return 'Category 1';
    } else if (formattedMorphologyCode) {
      return 'Category 2';
    }
    return '';
  };

  useEffect(() => {
    if (!tenantConfigData?.tenantConfig?.treatmentInfoPageDelayCategory) return;

    if (primaryDiagnosis) {
      const newDelayCategory = getNewDelayCategory();
      if (
        newDelayCategory !== treatmentInfo?.delayCategory &&
        (!treatmentInfo.isDelayCategoryManual || (newDelayCategory !== '' && newDelayCategory !== null))
      ) {
        updateHandler({ delayCategory: newDelayCategory, isDelayCategoryManual: false });
      }
    }
  }, [primaryDiagnosis, treatmentInfo.intent]);

  useEffect(() => {
    // Some absolute jank to get around the initial formik values being null
    if (chemoRegime === undefined) setChemoRegime(treatmentInfo?.chemoRegime);
    if (brachyDose === undefined) setBrachyDose(treatmentInfo?.brachyDose);
    if (customChemoWhere === undefined) setCustomChemoWhere(treatmentInfo?.customChemoWhere);
    if (startDateAdditionalInformation === undefined)
      setStartDateAdditionalInformation(treatmentInfo?.startDateAdditionalInformation);
    if (previousTreatmentAt === undefined) setPreviousTreatmentAt(treatmentInfo?.previousTreatmentAt);
    if (previousTreatmentAdditionalInformation === undefined)
      setPreviousTreatmentAdditionalInformation(treatmentInfo?.previousTreatmentAdditionalInformation);
    if (inPatientAt === undefined) setInPatientAt(treatmentInfo?.inPatientAt);
    if (otherCiedInstructions === undefined) setOtherCiedInstructions(treatmentInfo?.otherCiedInstructions);
    if (hormonesRegime === undefined) setHormonesRegime(treatmentInfo?.hormonesRegime);
    if (chiefComplaint === undefined) setChiefComplaint(treatmentInfo?.chiefComplaint);
    if (impressionPlan === undefined) setImpressionPlan(treatmentInfo?.impressionPlan);
    if (pharmacyPrescriptionOther === undefined) setPharmacyPrescriptionOther(treatmentInfo?.pharmacyPrescriptionOther);
    if (proximityToPreviousTreatment === undefined)
      setProximityToPreviousTreatment(treatmentInfo?.proximityToPreviousTreatment);
  }, [treatmentInfo]);

  const cardioOncValue =
    treatmentInfo &&
    (!treatmentInfo?.cardioonc ? (patientIsInSA && tumourStreamHasDefault ? 'no' : '') : treatmentInfo?.cardioonc);
  const endoscopeValue = !treatmentInfo?.endoscope
    ? patientIsInSA && tumourStreamIsHN
      ? 'no'
      : ''
    : treatmentInfo?.endoscope;

  if (treatmentInfo && (cardioOncState !== cardioOncValue || endoscopeState !== endoscopeValue)) {
    setCardioOncState(cardioOncValue);
    setEndoscopeState(endoscopeValue);
    setFieldValue('treatmentInfo.cardioonc', cardioOncValue);
    setFieldValue('treatmentInfo.endoscope', endoscopeValue);
    updateTreatmentInfo({
      variables: { id: careplanId, cardioonc: cardioOncValue, endoscope: endoscopeValue },
    });
  }

  const updateHandler = (changedValues: { [key: string]: string | null | boolean }): void => {
    const fieldKeys = Object.keys(changedValues);

    fieldKeys.forEach((key) => setFieldValue(`treatmentInfo.${key}`, changedValues[key]));
    updateTreatmentInfo({
      variables: {
        id: careplanId,
        ...changedValues,
      },
    });
  };

  const isBackButtonDisabled = () => {
    if (isSimpleDiagnosisSelectionEnabled && diagnosisData) return false;
    if (!diagnosisData) return true;
    if (diagnosisData.careplan.isMetsDiagnosisOwner || diagnosisData.careplan.isPrimDiagnosisOwner) return false;
    if (['draft', 'dx_submitted'].includes(diagnosisData.careplan.careplanStatus.toLowerCase())) return false;
    return true;
  };

  if (listRefLoading && !listRefData) return <LoadingSpinner />;

  return (
    <>
      <div className="main-container-parent-wrapper">
        <div className="main-container-wrapper">
          <CareplanBanners
            data={careplan}
            forceUpdate={() => {
              forceUpdate();
            }}
            refetchQueriesList={[
              { query: GET_DIAGNOSIS_FROM_CAREPLAN, variables: { careplanId } },
              { query: GET_CAREPLAN, variables: { id: careplanId } },
            ]}
          />
          <CareplanContainer>
            <div className="page-header">
              <Typography variant="h5">Treatment Info</Typography>
              {isAIPrepopulateEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: 1 }}>
                  <Typography variant="body1">
                    Please review the prepopulated values. You can view the source documents by clicking on
                  </Typography>
                  <FindInPageOutlinedIcon sx={{ color: 'primary.main' }} />
                </Box>
              )}
            </div>
            <Box component="form" id="treatmentInfoForm" noValidate>
              {tenantAppConfig.TreatmentInfoPage.ReadyForCareDate && (
                <RODatePicker
                  id="readyForCareDate"
                  fieldlabel="Ready for Care Date"
                  value={treatmentInfo.readyForCareDate ? dayjs(treatmentInfo.readyForCareDate) : null}
                  error={!isFieldValid(treatmentInfo.readyForCareDate)}
                  required
                  onChange={(date: Dayjs | null, context: any) => {
                    if (context.validationError) {
                      setSelectedDateError(context.validationError);
                      return;
                    }
                    const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                    updateHandler({ readyForCareDate: dateString });
                  }}
                  onError={setSelectedDateError}
                />
              )}
              <ROAutocomplete
                id="intent"
                fieldlabel="Intent"
                options={state.intentOptions}
                value={getOptionByValue(
                  state.intentOptions.map((data) => ({ label: data.label, value: data.value })),
                  treatmentInfo.intent,
                )}
                inputProps={{
                  error: !isFieldValid(treatmentInfo.intent),
                  helperText: isFieldValid(treatmentInfo.intent) ? '' : 'This field is required',
                }}
                required
                onChange={(option: SelectOptionType | string) => {
                  const value = typeof option === 'string' ? option : option?.value;
                  updateHandler({ intent: value || '' });
                }}
              />
              <ROAutocomplete
                id="priority"
                fieldlabel={tenantAppConfig.Tooltips.hasPriority ? 'Treatment Start Priority' : 'Priority'}
                options={state.priorityOptions}
                value={getOptionByValue(
                  state.priorityOptions.map((data) => ({ label: data.label, value: data.value })),
                  treatmentInfo.priority,
                )}
                inputProps={{
                  error: !isFieldValid(treatmentInfo.priority),
                  helperText: isFieldValid(treatmentInfo.priority) ? '' : 'This field is required',
                }}
                onChange={(option: SelectOptionType | string) => {
                  const value = typeof option === 'string' ? option : option?.value;
                  updateHandler({ priority: value || '' });
                }}
                required
                tooltip={tenantAppConfig.Tooltips.hasPriority ? tooltipIconWrapper(priorityTooltip) : undefined}
              />
              {tenantAppConfig.TreatmentInfoPage.ConsentStatus && (
                <ROAutocomplete
                  id="consentStatus"
                  fieldlabel={'Consent status'}
                  options={state.consentOptions}
                  value={getOptionByValue(
                    state.consentOptions.map((data) => ({ label: data.label, value: data.value })),
                    treatmentInfo.consentStatus,
                  )}
                  inputProps={{
                    error: !isFieldValid(treatmentInfo.consentStatus),
                    helperText: isFieldValid(treatmentInfo.consentStatus) ? '' : 'This field is required',
                  }}
                  onChange={(option: SelectOptionType | string) => {
                    const value = typeof option === 'string' ? option : option?.value;
                    updateHandler({ consentStatus: value || '' });
                  }}
                  required
                />
              )}
              {tenantAppConfig.TreatmentInfoPage.PharmacyPrescription && (
                <ROAutocomplete
                  id="pharmacyPrescription"
                  fieldlabel="Pharmacy prescription"
                  options={state.pharmacyPrescriptionOptions}
                  value={getOptionByValue(
                    state.pharmacyPrescriptionOptions.map((data) => ({ label: data.label, value: data.value })),
                    treatmentInfo.pharmacyPrescription,
                  )}
                  inputProps={{
                    error: !isFieldValid(treatmentInfo.pharmacyPrescription),
                    helperText: isFieldValid(treatmentInfo.pharmacyPrescription) ? '' : 'This field is required',
                  }}
                  onChange={(option: SelectOptionType | string) => {
                    const value = typeof option === 'string' ? option : option?.value;
                    updateHandler({ pharmacyPrescription: value || '' });
                    if (value !== 'Other') {
                      setPharmacyPrescriptionOther('');
                    }
                  }}
                  required
                />
              )}
              {tenantAppConfig.TreatmentInfoPage.PharmacyPrescription &&
                treatmentInfo.pharmacyPrescription === 'Other' && (
                  <ROTextField
                    id="pharmacyPrescriptionOther"
                    fieldlabel="Pharmacy prescription other"
                    value={pharmacyPrescriptionOther ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setPharmacyPrescriptionOther(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({ pharmacyPrescriptionOther });
                    }}
                  />
                )}
              {tenantAppConfig.TreatmentInfoPage.TreatmentStartDate && (
                <RODatePicker
                  id="preferredTreatmentStartDate"
                  disablePast
                  value={
                    treatmentInfo.preferredTreatmentStartDate ? dayjs(treatmentInfo.preferredTreatmentStartDate) : null
                  }
                  onChange={(date: Dayjs | null, context: any) => {
                    if (context.validationError) {
                      setSelectedDateError(context.validationError);
                      return;
                    }
                    const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                    updateHandler({ preferredTreatmentStartDate: dateString });
                  }}
                  onError={setSelectedDateError}
                  fieldlabel="Preferred Treatment Start Date"
                />
              )}
              {tenantAppConfig.TreatmentInfoPage.AdditionalBookingInfo && (
                <ROTextField
                  id="startDateAdditionalInformation"
                  fieldlabel="Additional Booking Information"
                  value={startDateAdditionalInformation ?? ''}
                  multiline
                  maxRows={4}
                  placeholder="e.g. start treatment two weeks after Easter holidays"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                    setStartDateAdditionalInformation(event.target.value);
                  }}
                  onBlur={(): void => {
                    updateHandler({ startDateAdditionalInformation });
                  }}
                />
              )}
              <ROAutocomplete
                id="treatingDepartment"
                fieldlabel="Treating Department"
                options={state.treatingDepartmentOptions}
                value={getOptionByValue(
                  state.treatingDepartmentOptions.map((data) => ({ label: data.label, value: data.value })),
                  treatmentInfo.treatingDepartment,
                )}
                required
                onChange={(option: SelectOptionType | string) => {
                  const value = typeof option === 'string' ? option : option?.value;
                  updateHandler({
                    treatingDepartment: value || '',
                  });
                }}
                inputProps={{
                  error: !isFieldValid(treatmentInfo.treatingDepartment),
                  helperText: isFieldValid(treatmentInfo.treatingDepartment) ? '' : 'This field is required',
                }}
              />
              {tenantAppConfig.TreatmentInfoPage.MDT && (
                <ROAutocomplete
                  id="previousMdt"
                  fieldlabel="Has patient been through a MDT?"
                  options={state.emdtOptions}
                  value={getOptionByValue(
                    state.emdtOptions.map((data) => ({ label: data.label, value: data.value })),
                    treatmentInfo.previousMdt,
                  )}
                  inputProps={{
                    error: !isFieldValid(treatmentInfo.previousMdt),
                    helperText: isFieldValid(treatmentInfo.treatingDepartment) ? '' : 'This field is required',
                  }}
                  required
                  onChange={(option: SelectOptionType | string) => {
                    const value = typeof option === 'string' ? option : option?.value;
                    updateHandler({ previousMdt: value || '' });
                  }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ROToggleButtons
                  id="previousRadioTreatment"
                  fieldlabel="Previous Radiotherapy Treatment"
                  required
                  options={tenantAppConfig.QuestionOptions.previousRadioTreatment}
                  value={treatmentInfo.previousRadioTreatment}
                  error={!isFieldValid(treatmentInfo.previousRadioTreatment)}
                  handleChange={(value: string): void => {
                    updateHandler({
                      previousRadioTreatment: value,
                      previousCompositePlanRequired: '',
                      previousTreatmentAt: '',
                      previousTreatmentAdditionalInformation: '',
                      proximityToPreviousTreatment: '',
                    });
                    setPreviousTreatmentAt('');
                    setPreviousTreatmentAdditionalInformation('');
                    setProximityToPreviousTreatment('');
                  }}
                />
                {isAIPrepopulateEnabled &&
                  (treatmentInfoPrepopulate?.previousRadioTreatment?.citations?.length ?? 0) > 0 && (
                    <CitationButton
                      onClick={(): void =>
                        openCitation(
                          'Previous radiotherapy',
                          (treatmentInfoPrepopulate?.previousRadioTreatment?.citations ??
                            []) as TreatmentInfoCitationItem[],
                        )
                      }
                      sx={{ mb: 3 }}
                    />
                  )}
              </Box>
              {treatmentInfo.previousRadioTreatment === 'yes' && (
                <>
                  <ROAutocomplete
                    id="proximityToPreviousTreatment"
                    fieldlabel="Proximity"
                    options={PROXIMITY_OPTIONS}
                    value={getOptionByValue(
                      PROXIMITY_OPTIONS.map((data) => ({ label: data.label, value: data.value })),
                      proximityToPreviousTreatment,
                    )}
                    onChange={(option: SelectOptionType | string) => {
                      const value = typeof option === 'string' ? option : option?.value;
                      setProximityToPreviousTreatment(value || '');
                      updateHandler({ proximityToPreviousTreatment: value || '' });
                    }}
                    required
                    inputProps={{
                      error: !isFieldValid(treatmentInfo.proximityToPreviousTreatment),
                      helperText: isFieldValid(treatmentInfo.proximityToPreviousTreatment)
                        ? ''
                        : 'This field is required',
                    }}
                  />
                  <ROToggleButtons
                    id="previousCompositePlanRequired"
                    fieldlabel="Is a Composite Plan Required"
                    required
                    options={BOOLEAN_OPTIONS}
                    value={treatmentInfo.previousCompositePlanRequired}
                    error={!isFieldValid(treatmentInfo.previousCompositePlanRequired)}
                    handleChange={(value: string): void => {
                      updateHandler({ previousCompositePlanRequired: value });
                    }}
                  />
                  <ROTextField
                    id="previousTreatmentAt"
                    fieldlabel="Previous Treatment Centre & Date"
                    placeholder="Please specify centre & year if known"
                    value={previousTreatmentAt ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setPreviousTreatmentAt(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({ previousTreatmentAt });
                    }}
                  />
                  <ROTextField
                    id="previousTreatmentAdditionalInformation"
                    fieldlabel="Additional Information"
                    placeholder="e.g. composite dose information"
                    value={previousTreatmentAdditionalInformation ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setPreviousTreatmentAdditionalInformation(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({
                        previousTreatmentAdditionalInformation,
                      });
                    }}
                  />
                </>
              )}

              <ROToggleButtons
                id="patientStatus"
                fieldlabel="Patient Status"
                options={PATIENT_STATUS_OPTIONS}
                value={
                  treatmentInfo.patientStatus ? treatmentInfo.patientStatus : tenantAppConfig.DefaultValue.patientStatus
                }
                handleChange={(value: string): void => {
                  const inPatientAt =
                    practitionerTenant.toLowerCase() === 'troc' && value === 'inPatient' ? "St. Joseph's" : '';
                  updateHandler({
                    patientStatus: value,
                    inPatientAt: inPatientAt,
                  });
                  setInPatientAt(inPatientAt);
                }}
              />
              {treatmentInfo.patientStatus === 'inPatient' && (
                <ROTextField
                  id="inPatientAt"
                  placeholder="In patient at.."
                  value={inPatientAt ?? ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                    setInPatientAt(event.target.value);
                  }}
                  onBlur={(): void => {
                    updateHandler({ inPatientAt });
                  }}
                />
              )}
              {tenantConfigData?.tenantConfig?.treatmentInfoPageDelayCategory && (
                <ROToggleButtons
                  id="delayCategory"
                  fieldlabel="RCR Treatment Delay Priority"
                  required
                  error={!isFieldValid(treatmentInfo.delayCategory, 'delayCategory')}
                  options={DELAY_CATEGORY_OPTIONS}
                  value={treatmentInfo.delayCategory ? treatmentInfo.delayCategory : 'Not Applicable'}
                  handleChange={(value: string): void => {
                    updateHandler({ delayCategory: value, isDelayCategoryManual: true });
                  }}
                  tooltip={
                    <StyledQuestionCircleIcon
                      color="info"
                      fontSize="small"
                      onClick={() => {
                        setRCRModalStatus(true);
                      }}
                    />
                  }
                />
              )}
              {patientDetails && patientDetails.patientGender !== Gender.MALE && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ROToggleButtons
                    id="pregnancy"
                    fieldlabel="Pregnancy"
                    options={BOOLEAN_UNKNOWN_OPTIONS}
                    value={treatmentInfo.pregnancy ? treatmentInfo.pregnancy : 'Unknown'}
                    error={!treatmentInfo.pregnancy}
                    handleChange={(value: string): void => {
                      updateHandler({ pregnancy: value });
                    }}
                  />
                  {isAIPrepopulateEnabled && (treatmentInfoPrepopulate?.pregnancy?.citations?.length ?? 0) > 0 && (
                    <CitationButton
                      onClick={(): void =>
                        openCitation(
                          'Pregnancy',
                          (treatmentInfoPrepopulate?.pregnancy?.citations ?? []) as TreatmentInfoCitationItem[],
                        )
                      }
                      sx={{ mb: 3 }}
                    />
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ROToggleButtons
                  id="cied"
                  fieldlabel={tenantAppConfig.QuestionLabel.CIED}
                  options={tenantAppConfig.QuestionOptions.CIED}
                  value={treatmentInfo.cied}
                  error={(treatmentInfo.cied === 'icd' && !ICDTAuthorized) || !isFieldValid(treatmentInfo.cied, 'CIED')}
                  // This conditional error message is to provide feedback to non-gc ROs in the UK that explains why ICD
                  // is not supported for the CIED field. Ideally this would become a tool tip in the future
                  warning={treatmentInfo.cied === 'yes' && region !== Region.UK}
                  helperText={
                    treatmentInfo.cied === 'yes' && region !== Region.UK
                      ? 'Warning: Patient has Cardiac Implantable Electronic Device (CIED)'
                      : treatmentInfo.cied === 'icd' && !ICDTAuthorized
                      ? 'The patient cannot be treated at GenesisCare, as per the ICD protocol.'
                      : undefined
                  }
                  helperTextSx={
                    treatmentInfo.cied === 'yes' && region !== Region.UK ? { fontSize: '0.7rem' } : undefined
                  }
                  handleChange={(value: string): void => {
                    const mutationVariables =
                      value !== 'yes'
                        ? tenantAppConfig.ResetMutationVariableDefaults.CIED
                        : tenantAppConfig.GetMutationVariableDefaults.CIED;
                    updateHandler({ cied: value, ...mutationVariables });
                    setOtherCiedInstructions('');
                  }}
                  required
                />
                {isAIPrepopulateEnabled && (treatmentInfoPrepopulate?.cied?.citations?.length ?? 0) > 0 && (
                  <CitationButton
                    onClick={(): void =>
                      openCitation(
                        'CIED',
                        (treatmentInfoPrepopulate?.cied?.citations ?? []) as TreatmentInfoCitationItem[],
                      )
                    }
                    sx={{
                      mb: treatmentInfoPrepopulate?.cied?.value === 'yes' ? 6 : 2,
                    }}
                  />
                )}
              </Box>
              {treatmentInfo.cied === 'yes' && region !== Region.UK && (
                <>
                  <ROToggleButtons
                    id="ciedProtocolRequired"
                    fieldlabel="CIED Protocol Required"
                    options={BOOLEAN_OPTIONS}
                    required
                    value={treatmentInfo.ciedProtocolRequired}
                    error={!isFieldValid(treatmentInfo.ciedProtocolRequired)}
                    handleChange={(value: string): void => {
                      const mutationVariables =
                        value !== 'yes'
                          ? tenantAppConfig.ResetMutationVariableDefaults.CiedProtocolRequired
                          : tenantAppConfig.GetMutationVariableDefaults.CiedProtocolRequired;
                      updateHandler({ ciedProtocolRequired: value, ...mutationVariables });
                    }}
                  />
                  {treatmentInfo.cied === 'yes' && treatmentInfo.ciedProtocolRequired === 'yes' && (
                    <>
                      <ROToggleButtons
                        id="preTreatmentCiedCheckRequired"
                        fieldlabel="Pre-treatment CIED Check Required"
                        options={BOOLEAN_OPTIONS}
                        value={treatmentInfo.preTreatmentCiedCheckRequired}
                        required
                        error={!isFieldValid(treatmentInfo.preTreatmentCiedCheckRequired)}
                        handleChange={(value: string): void => {
                          updateHandler({ preTreatmentCiedCheckRequired: value });
                        }}
                      />
                      {tenantAppConfig.CardioReferralField && treatmentInfo.ciedProtocolRequired === 'yes' && (
                        <ROToggleButtons
                          id="referralToGcCardiologist"
                          fieldlabel="Referral to GC Cardiologist"
                          options={BOOLEAN_OPTIONS}
                          value={treatmentInfo.referralToGcCardiologist ? treatmentInfo.referralToGcCardiologist : 'no'}
                          error={!isFieldValid(treatmentInfo.referralToGcCardiologist)}
                          handleChange={(value: string): void => {
                            updateHandler({ referralToGcCardiologist: value });
                          }}
                        />
                      )}
                      {treatmentInfo.ciedProtocolRequired === 'yes' && (
                        <ROTextField
                          id="otherCiedInstructions"
                          fieldlabel="Other CIED Instructions"
                          value={otherCiedInstructions ?? ''}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                            setOtherCiedInstructions(event.target.value);
                          }}
                          onBlur={(): void => {
                            updateHandler({ otherCiedInstructions });
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {treatmentInfo.cied === 'cied' && region === Region.UK && (
                <>
                  <ROToggleButtons
                    id="ciedPatientDependancy"
                    fieldlabel="Is patient dependent on CIED?"
                    options={BOOLEAN_OPTIONS}
                    value={treatmentInfo.ciedPatientDependancy}
                    required
                    error={!isFieldValid(treatmentInfo.ciedPatientDependancy, 'ciedPatientDependency')}
                    handleChange={(value: string): void => {
                      updateHandler({ ciedPatientDependancy: value });
                    }}
                  />

                  <ROToggleButtons
                    id="ciedPatientLinac"
                    fieldlabel="Will patient be treated on MR Linac?"
                    options={BOOLEAN_OPTIONS}
                    value={treatmentInfo.ciedLinacRequired}
                    required
                    error={
                      !isFieldValid(treatmentInfo.ciedLinacRequired, 'ciedLinacRequired') ||
                      treatmentInfo.ciedLinacRequired === 'yes'
                    }
                    helperText={
                      treatmentInfo.ciedLinacRequired === 'yes'
                        ? 'The patient cannot be treated at GenesisCare, as per the CIED protocol.'
                        : treatmentInfo.ciedLinacRequired === 'no'
                        ? 'Please send Pacing Report to GenesisCare.'
                        : undefined
                    }
                    info={treatmentInfo.ciedLinacRequired === 'no'}
                    handleChange={(value: string): void => {
                      updateHandler({ ciedLinacRequired: value });
                    }}
                  />
                </>
              )}
              {patientIsInSA && (
                <ROToggleButtons
                  id="cardioonc"
                  fieldlabel={'CardioOnc Referral Required'}
                  error={!isFieldValid(cardioOncState)}
                  required
                  options={BOOLEAN_OPTIONS}
                  value={treatmentInfo.cardioonc ? treatmentInfo.cardioonc : tumourStreamHasDefault ? 'no' : ''}
                  handleChange={(value: string): void => {
                    updateHandler({ cardioonc: value });
                  }}
                />
              )}
              {patientIsInSA && tumourStreamIsHN && (
                <ROToggleButtons
                  id="endoscope"
                  fieldlabel={'Endoscope Required'}
                  error={!isFieldValid(endoscopeState)}
                  required
                  options={BOOLEAN_OPTIONS}
                  value={treatmentInfo.endoscope ? treatmentInfo.endoscope : 'no'}
                  handleChange={(value: string): void => {
                    updateHandler({ endoscope: value });
                  }}
                />
              )}
              <ROToggleButtons
                id="chemotherapy"
                fieldlabel={'Chemotherapy'}
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={treatmentInfo.chemotherapy ? treatmentInfo.chemotherapy : 'Unknown'}
                handleChange={(value: string): void => {
                  updateHandler({
                    chemotherapy: value,
                    chemoRegime: '',
                    chemoWhere: '',
                    customChemoWhere: '',
                    chemoSequencing: '',
                    chemoStartDate: '',
                    chemoEndDate: '',
                  });
                  setCustomChemoWhere('');
                }}
              />
              {treatmentInfo.chemotherapy === 'yes' && (
                <>
                  <ROTextField
                    id="chemoRegime"
                    fieldlabel="Chemo Regimen"
                    value={chemoRegime ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setChemoRegime(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({ chemoRegime });
                    }}
                  />
                  <ROAutocomplete
                    id="chemoWhere"
                    fieldlabel="Chemo Where"
                    options={state.chemoWhereOptions}
                    value={getOptionByValue(
                      state.chemoWhereOptions.map((data) => ({ label: data.label, value: data.value })),
                      treatmentInfo.chemoWhere,
                    )}
                    onChange={(option: SelectOptionType | string) => {
                      const value = typeof option === 'string' ? option : option?.value;
                      updateHandler({
                        chemoWhere: value || '',
                        customChemoWhere: '',
                      });
                      setCustomChemoWhere('');
                    }}
                  />

                  {treatmentInfo.chemoWhere && treatmentInfo.chemoWhere.toLowerCase() === 'other' && (
                    <ROTextField
                      id="customChemoWhere"
                      fieldlabel="Specify Chemo Where"
                      value={customChemoWhere ?? ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setCustomChemoWhere(event.target.value);
                      }}
                      onBlur={(): void => {
                        updateHandler({ customChemoWhere });
                      }}
                    />
                  )}
                  <ROAutocomplete
                    id="chemoSequencing"
                    fieldlabel="Chemo Sequencing"
                    options={state.chemoSequencingOptions}
                    value={getOptionByValue(
                      state.chemoSequencingOptions.map((data) => ({ label: data.label, value: data.value })),
                      treatmentInfo.chemoSequencing,
                    )}
                    onChange={(option: SelectOptionType | string) => {
                      const value = typeof option === 'string' ? option : option?.value;
                      updateHandler({
                        chemoSequencing: value || '',
                      });
                    }}
                  />
                  <RODatePicker
                    id="chemoStartDate"
                    fieldlabel="Chemo Start Date"
                    value={treatmentInfo.chemoStartDate ? dayjs(treatmentInfo.chemoStartDate) : null}
                    maxDate={dayjs(treatmentInfo.chemoEndDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ chemoStartDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                  <RODatePicker
                    id="chemoEndDate"
                    fieldlabel="Chemo End Date"
                    value={treatmentInfo.chemoEndDate ? dayjs(treatmentInfo.chemoEndDate) : null}
                    minDate={dayjs(treatmentInfo.chemoStartDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ chemoEndDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                </>
              )}
              {tenantAppConfig.TreatmentInfoPage.Hormones && (
                <ROToggleButtons
                  id="hormones"
                  fieldlabel="Hormones"
                  options={BOOLEAN_UNKNOWN_OPTIONS}
                  value={treatmentInfo.hormones || 'unknown'}
                  handleChange={(value: string): void => {
                    updateHandler({ hormones: value, hormonesRegime: '', hormonesStartDate: '', hormonesEndDate: '' });
                    setHormonesRegime('');
                  }}
                />
              )}
              {tenantAppConfig.TreatmentInfoPage.Hormones && treatmentInfo.hormones === 'yes' && (
                <>
                  <ROTextField
                    id="hormonesRegime"
                    fieldlabel="Hormones Regime"
                    value={hormonesRegime ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setHormonesRegime(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({ hormonesRegime });
                    }}
                  />
                  <RODatePicker
                    id="hormonesStartDate"
                    fieldlabel="Hormones Start Date"
                    value={treatmentInfo.hormonesStartDate ? dayjs(treatmentInfo.hormonesStartDate) : null}
                    maxDate={dayjs(treatmentInfo.hormonesEndDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ hormonesStartDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                  <RODatePicker
                    id="hormonesEndDate"
                    fieldlabel="Hormones End Date"
                    value={treatmentInfo.hormonesEndDate ? dayjs(treatmentInfo.hormonesEndDate) : null}
                    minDate={dayjs(treatmentInfo.hormonesStartDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ hormonesEndDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                </>
              )}
              {tenantAppConfig.TreatmentInfoPage.Brachytherapy && (
                <ROToggleButtons
                  id="brachytherapy"
                  fieldlabel="Brachytherapy"
                  options={BOOLEAN_UNKNOWN_OPTIONS}
                  value={treatmentInfo.brachytherapy ? treatmentInfo.brachytherapy : 'unknown'}
                  handleChange={(value: string): void => {
                    updateHandler({
                      brachytherapy: value,
                      brachyWhere: '',
                      brachySequencing: '',
                      brachyDose: '',
                      brachyStartDate: '',
                      brachyEndDate: '',
                    });
                    setBrachyDose('');
                  }}
                />
              )}
              {tenantAppConfig.TreatmentInfoPage.Brachytherapy && treatmentInfo.brachytherapy === 'yes' && (
                <>
                  <ROAutocomplete
                    id="brachyWhere"
                    fieldlabel="Brachy Where"
                    options={state.brachyWhereOptions}
                    value={getOptionByValue(
                      state.brachyWhereOptions.map((data) => ({ label: data.label, value: data.value })),
                      treatmentInfo.brachyWhere,
                    )}
                    onChange={(option: SelectOptionType | string) => {
                      const value = typeof option === 'string' ? option : option?.value;
                      updateHandler({
                        brachyWhere: value || '',
                      });
                    }}
                  />
                  <ROAutocomplete
                    id="brachySequencing"
                    fieldlabel="Brachy Sequencing"
                    options={state.brachySequencingOptions}
                    value={getOptionByValue(
                      state.brachySequencingOptions.map(
                        (data): ListOptionType => ({
                          label: data.label,
                          value: data.value,
                        }),
                      ),
                      treatmentInfo.brachySequencing,
                    )}
                    onChange={(option: SelectOptionType | string) => {
                      const value = typeof option === 'string' ? option : option?.value;
                      updateHandler({
                        brachySequencing: value || '',
                      });
                    }}
                  />
                  <ROTextField
                    id="brachyDose"
                    fieldlabel="Brachy Dose"
                    value={brachyDose ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setBrachyDose(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({ brachyDose });
                    }}
                  />
                  <RODatePicker
                    id="brachyStartDate"
                    fieldlabel="Brachy Start Date"
                    value={treatmentInfo.brachyStartDate ? dayjs(treatmentInfo.brachyStartDate) : null}
                    maxDate={dayjs(treatmentInfo.brachyEndDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ brachyStartDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                  <RODatePicker
                    id="brachyEndDate"
                    fieldlabel="Brachy End Date"
                    value={treatmentInfo.brachyEndDate ? dayjs(treatmentInfo.brachyEndDate) : null}
                    minDate={dayjs(treatmentInfo.brachyStartDate)}
                    onChange={(date: Dayjs | null, context: any) => {
                      if (context.validationError) {
                        setSelectedDateError(context.validationError);
                        return;
                      }
                      const dateString = date ? dayjs(date).format('YYYY-MM-DD') : '';
                      updateHandler({ brachyEndDate: dateString });
                    }}
                    onError={setSelectedDateError}
                  />
                </>
              )}
              {tenantAppConfig.TreatmentInfoPage.PerformanceStatus && (
                <>
                  <ROToggleButtons
                    id="performanceScaleType"
                    fieldlabel="Performance Status"
                    options={SCALE_OPTIONS}
                    value={treatmentInfo.performanceScaleType}
                    handleChange={(value: string): void => {
                      updateHandler({
                        performanceScaleType: value || '',
                        performanceScaleRating: null,
                      });
                    }}
                  />
                  {['ecog', 'kps'].includes(treatmentInfo.performanceScaleType) && (
                    <ROAutocomplete
                      id="performanceScaleRating"
                      disablePortal
                      fieldlabel={treatmentInfo.performanceScaleType === 'ecog' ? 'ECOG' : 'Karnofsky'}
                      options={
                        treatmentInfo.performanceScaleType === 'ecog'
                          ? ECOG_PERFORMANCE_STATUS_OPTIONS
                          : KPS_PERFORMANCE_STATUS_OPTIONS
                      }
                      value={treatmentInfo.performanceScaleRating}
                      required
                      onChange={(option: SelectOptionType | string) => {
                        const value = typeof option === 'string' ? option : option?.value;
                        updateHandler({
                          performanceScaleRating: value || '',
                        });
                      }}
                      inputProps={{
                        error: !isFieldValid(treatmentInfo.performanceScaleRating),
                        helperText: isFieldValid(treatmentInfo.performanceScaleRating) ? '' : 'This field is required',
                      }}
                      tooltip={
                        treatmentInfo.performanceScaleType === 'kps' ? (
                          <StyledQuestionCircleIcon
                            color="info"
                            fontSize="small"
                            onClick={() => {
                              setShowKarnofskyModal(true);
                            }}
                          />
                        ) : undefined
                      }
                    />
                  )}
                </>
              )}
              {tenantAppConfig.TreatmentInfoPage.ccip && (
                <>
                  <ROTextField
                    id="chiefComplaint"
                    fieldlabel="Chief Complaint"
                    value={chiefComplaint ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setChiefComplaint(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({
                        chiefComplaint,
                      });
                    }}
                    minRows={4}
                    maxRows={8}
                    multiline
                    maxLength={2000}
                  />
                  <ROTextField
                    id="impressionPlan"
                    fieldlabel="Impression/Plan"
                    value={impressionPlan ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setImpressionPlan(event.target.value);
                    }}
                    onBlur={(): void => {
                      updateHandler({
                        impressionPlan,
                      });
                    }}
                    minRows={4}
                    maxRows={8}
                    multiline
                    maxLength={2000}
                  />
                </>
              )}
            </Box>
          </CareplanContainer>
          <ROPatientCarePlanPageFooter
            onBack={(): void => {
              const backPage = previousURL(diagnosis, diagnosisData?.careplan, patientId, careplanId, careplanState);
              if (backPage === '') return;
              history.push(backPage);
              return;
            }}
            onNext={(): void => {
              setPressedContinue(true);
              forceUpdate();
              ScrollToError();
              if (!isPageValid(validationDependencyStructure, treatmentInfo)) return;

              // Ensure that user can't press Next when non-mandatory fields are filled and fail validation
              if (selectedDateError) return;

              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/templateSelection`);
            }}
            nextDisabled={!careplanId}
            backDisabled={isBackButtonDisabled()}
            backButtonId={'treatment-info-back-button'}
          />
        </div>
        <ROPatientCarePathSidePanel />
      </div>
      <StandardDialog
        open={showKarnofskyModal}
        maxWidth="xl"
        onClose={(): void => {
          setShowKarnofskyModal(false);
        }}
        showDividers
        title="Karnofsky Performance Status Scale Definitions Rating (%) Criteria"
        enableActionPopover>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="45%">Ability Level</TableCell>
                <TableCell width="10%">{'Rating (%)'}</TableCell>
                <TableCell width="45%">Criteria</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {KPS_PERFORMANCE_STATUS_OPTIONS.map(({ label }, index) => {
                const rating = label.split(' - ')[0];
                const criteria = label.split(' - ')[1];

                // This jank is just to prevent the Table Row being defined 10 times
                let rowSpan = 0;
                let abilityLevel = '';
                if (index === 0) {
                  rowSpan = KARNOFSKY_ABILITY_LEVELS[0].rowSpan;
                  abilityLevel = KARNOFSKY_ABILITY_LEVELS[0].abilityLevel;
                } else if (index === 3) {
                  rowSpan = KARNOFSKY_ABILITY_LEVELS[1].rowSpan;
                  abilityLevel = KARNOFSKY_ABILITY_LEVELS[1].abilityLevel;
                } else if (index === 7) {
                  rowSpan = KARNOFSKY_ABILITY_LEVELS[2].rowSpan;
                  abilityLevel = KARNOFSKY_ABILITY_LEVELS[2].abilityLevel;
                }

                return (
                  <TableRow key={label}>
                    {abilityLevel ? <TableCell rowSpan={rowSpan}>{abilityLevel}</TableCell> : null}
                    <TableCell>{rating}</TableCell>
                    <TableCell>{criteria}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </StandardDialog>
      <StandardDialog
        enableActionPopover
        maxWidth="lg"
        open={rcrModalStatus}
        onClose={setRCRModalStatus}
        showDividers
        title="RCR Treatment Delay Categories of Priority">
        <ModalBody />
      </StandardDialog>
      <CitationOverlay
        open={citationOverlay.open}
        citations={citationOverlay.citations}
        activeIndex={citationOverlay.activeIndex}
        onClose={(): void => setCitationOverlay({ open: false, citations: [], activeIndex: 0 })}
        onActiveIndexChange={(nextIndex: number): void =>
          setCitationOverlay((prev) => ({ ...prev, activeIndex: nextIndex }))
        }
      />
    </>
  );
};

export default ROPatientTreatmentInfoPage;
